/**
 * Team round tag choice — shared parsing, eligibility, bucket scan, tag pooling.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { usedAtVenueRecently } from "./venues";
import { isSimulatorTestTeam } from "./TestDataGenerator";

/** Real player teams only — simulator / test NPCs cannot choose round tags */
export function filterTeamsForRoundChooser(teamNames) {
  if (!Array.isArray(teamNames)) return [];
  return teamNames.filter((t) => t && !isSimulatorTestTeam(t));
}

export const ROUND_TAG_SESSION_PATH = "roundTagChoice/session";

/** Canonical genres aligned with csvquizedit / QuestionLibrary */
export const ROUND_TAG_GENRE_OPTIONS = [
  "Art",
  "Famous People",
  "Food and Drink",
  "Games",
  "Geography",
  "History",
  "Literature",
  "Movie",
  "Music",
  "Pop Culture",
  "Psychology",
  "Pop Culture/Weird",
  "Science",
  "Sport",
  "TV",
  "Weird",
  "Wordplay",
];

const TEAM_CHOICE_NORMALIZED = new Set(["teamchoice", "playerchoice"]);

export function extractHashtagsFromText(text) {
  if (!text || typeof text !== "string") return [];
  const trimmed = text.trim();
  if (!trimmed.startsWith("#")) return [];
  if (trimmed.includes("\n")) return [];
  const positions = [];
  for (let i = 0; i < trimmed.length; i += 1) {
    if (trimmed[i] === "#") positions.push(i);
  }
  const tags = [];
  for (let i = 0; i < positions.length; i += 1) {
    const start = positions[i] + 1;
    const end = i + 1 < positions.length ? positions[i + 1] : trimmed.length;
    const raw = trimmed.slice(start, end).trim();
    if (raw) tags.push(raw);
  }
  return tags;
}

export function resolveCanonicalGenre(parsedGenre, options = ROUND_TAG_GENRE_OPTIONS) {
  if (!parsedGenre || !options || !Array.isArray(options)) return parsedGenre || null;
  const trimmed = String(parsedGenre).trim();
  if (!trimmed) return null;
  const exact = options.find((g) => g === trimmed);
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  const caseMatch = options.find((g) => g.toLowerCase() === lower);
  if (caseMatch) return caseMatch;
  const noSpaces = lower.replace(/\s+/g, "");
  const spaceMatch = options.find((g) => g.replace(/\s+/g, "").toLowerCase() === noSpaces);
  if (spaceMatch) return spaceMatch;
  return trimmed;
}

/**
 * @returns {{ isTeamChoice: boolean, genreBucket: string | null }}
 * genreBucket: canonical genre string, or null for global (no genre hashtag on STARTROUND)
 */
export function parseStartRoundTeamChoice(q) {
  const text = q?.Q || q?.question || "";
  const tags = extractHashtagsFromText(typeof text === "string" ? text : "");
  const upperTypes = String(q?.type || q?.CAT || "").toUpperCase();
  if (upperTypes !== "STARTROUND") {
    return { isTeamChoice: false, genreBucket: null };
  }
  const normalizedTags = tags.map((t) => String(t).trim().toLowerCase().replace(/^#+/, ""));
  const hasTeamChoice = normalizedTags.some((t) => TEAM_CHOICE_NORMALIZED.has(t));
  if (!hasTeamChoice) {
    return { isTeamChoice: false, genreBucket: null };
  }
  let genreBucket = null;
  for (const raw of tags) {
    const canon = resolveCanonicalGenre(raw);
    if (canon && ROUND_TAG_GENRE_OPTIONS.includes(canon)) {
      const nt = String(raw).trim().toLowerCase().replace(/^#+/, "");
      if (TEAM_CHOICE_NORMALIZED.has(nt)) continue;
      genreBucket = canon;
      break;
    }
  }
  return { isTeamChoice: true, genreBucket };
}

export function bucketKeyFromGenre(genreBucket) {
  return genreBucket || "global";
}

/** Types that never receive team answers — skip when finding "last answerable" index */
const NON_ANSWERABLE_TYPES = new Set([
  "DISPLAY",
  "SLIDE",
  "DEFAULT",
  "DEFAULT CATEGORY",
  "STARTROUND",
  "ENDROUND",
  "SHOWROUNDS",
  "JOIN QUIZ",
  "INTRODUCTION",
  "END QUIZ",
  "BREAK TIME",
  "",
]);

function questionTypeUpper(q) {
  return String(q?.type || q?.CAT || "").trim().toUpperCase();
}

/** First question index of the round containing questionIdx (matches Quizmaster findRoundStart). */
export function findRoundStartQuestionIndex(questions, questionIdx) {
  if (!Array.isArray(questions) || questionIdx <= 0) return 0;
  for (let i = questionIdx; i >= 0; i -= 1) {
    const t = questionTypeUpper(questions[i]);
    if (t === "STARTROUND" || t === "JOIN QUIZ" || t === "INTRODUCTION") {
      return i + 1;
    }
  }
  return 0;
}

export function findRoundStartIndex(questions, endRoundIdx) {
  return findRoundStartQuestionIndex(questions, endRoundIdx);
}

/** Index of STARTROUND slide for this question, or -1 */
export function getStartRoundSlideIndexForQuestion(questions, questionIdx) {
  const firstQ = findRoundStartQuestionIndex(questions, questionIdx);
  if (firstQ <= 0) return -1;
  const slide = firstQ - 1;
  return questionTypeUpper(questions[slide]) === "STARTROUND" ? slide : -1;
}

/**
 * Highest index in [roundStartIdx, endRoundIdx) that can collect answers (last "real" slide before ENDROUND).
 */
export function getLastAnswerableQuestionIndexInRound(questions, roundStartIdx, endRoundIdx) {
  if (!Array.isArray(questions) || endRoundIdx <= 0) return -1;
  for (let i = endRoundIdx - 1; i >= roundStartIdx; i -= 1) {
    const t = questionTypeUpper(questions[i]);
    if (!NON_ANSWERABLE_TYPES.has(t)) {
      return i;
    }
  }
  return -1;
}

/**
 * Sum points per team for answers in [roundStart, endRound), same as Roundscores.
 */
export function aggregateRoundPointsByTeam(answers, teamNames, roundStartIdx, endRoundIdx) {
  const scoreByTeam = {};
  const currentTeams = new Set(teamNames);
  const relevant = answers.filter(
    (a) =>
      typeof a.points === "number" &&
      !Number.isNaN(a.points) &&
      typeof a.q === "number" &&
      a.q >= roundStartIdx &&
      a.q < endRoundIdx
  );
  relevant.forEach((a) => {
    if (!currentTeams.has(a.team)) return;
    scoreByTeam[a.team] = (scoreByTeam[a.team] || 0) + a.points;
  });
  return scoreByTeam;
}

/**
 * @param {{ answers: object[], teamNames: string[], roundStartIdx: number, endRoundIdx: number, questions: object[], behavior: 'fairShare'|'random', lastChooserTeam: string|null }} params
 * @returns {{ eligibleTeam: string|null, tieBreak: string, candidatesConsidered: string[] }}
 */
export function computeEligibleTeamForEndedRound({
  answers,
  teamNames,
  roundStartIdx,
  endRoundIdx,
  questions,
  behavior,
  lastChooserTeam,
}) {
  const realTeams = filterTeamsForRoundChooser(teamNames);
  if (realTeams.length === 0) {
    return { eligibleTeam: null, tieBreak: "none", candidatesConsidered: [] };
  }

  const lastQ = getLastAnswerableQuestionIndexInRound(questions, roundStartIdx, endRoundIdx);
  if (lastQ < 0) {
    return { eligibleTeam: null, tieBreak: "none", candidatesConsidered: [] };
  }

  const answeredLast = new Set();
  answers.forEach((a) => {
    if (typeof a.q === "number" && a.q === lastQ && a.team && realTeams.includes(a.team)) {
      answeredLast.add(a.team);
    }
  });

  if (answeredLast.size === 0) {
    let pool = [...realTeams];
    if (behavior === "fairShare" && lastChooserTeam && pool.length > 1) {
      const without = pool.filter((t) => t !== lastChooserTeam);
      if (without.length > 0) pool = without;
    }
    const idx = Math.floor(Math.random() * pool.length);
    const eligibleTeam = pool[idx] || null;
    return {
      eligibleTeam,
      tieBreak: "random",
      candidatesConsidered: pool,
    };
  }

  const scoreByTeam = aggregateRoundPointsByTeam(answers, realTeams, roundStartIdx, endRoundIdx);
  let candidates = realTeams.filter((t) => answeredLast.has(t));
  candidates.sort((a, b) => {
    const pa = scoreByTeam[a] ?? 0;
    const pb = scoreByTeam[b] ?? 0;
    if (pa !== pb) return pa - pb;
    return a.localeCompare(b);
  });

  const lowest = candidates.length ? scoreByTeam[candidates[0]] ?? 0 : 0;
  const tied = candidates.filter((t) => (scoreByTeam[t] ?? 0) === lowest);

  let pool = tied.slice();
  if (behavior === "fairShare" && lastChooserTeam && pool.length > 1) {
    const without = pool.filter((t) => t !== lastChooserTeam);
    if (without.length > 0) pool = without;
  }

  const idx = Math.floor(Math.random() * pool.length);
  const eligibleTeam = pool[idx] || null;
  return {
    eligibleTeam,
    tieBreak: tied.length > 1 ? "random" : "unique",
    candidatesConsidered: pool,
  };
}

/**
 * Count team-choice STARTROUND rows per bucket (global + each genre).
 */
export function scanTeamChoiceRoundCounts(questions) {
  const counts = new Map();
  if (!Array.isArray(questions)) return counts;
  questions.forEach((q) => {
    const { isTeamChoice, genreBucket } = parseStartRoundTeamChoice(q);
    if (!isTeamChoice) return;
    const key = bucketKeyFromGenre(genreBucket);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
}

export function requiredTagPoolSizeForBucket(roundsInBucket) {
  return roundsInBucket + 6;
}

/**
 * QuestionLibrary ids already used in this game (resolved placeholder slots on the game doc,
 * plus Test Mode's list), so a library question never comes up twice in one game.
 * `exceptIdx` leaves out the slot being resolved now.
 */
export function libraryIdsUsedInGame(gameData, exceptIdx = -1) {
  const ids = new Set();
  const g = gameData || {};
  const qs = Array.isArray(g.questions) ? g.questions : g.questions ? Object.values(g.questions) : [];
  qs.forEach((q, i) => {
    const id = q?._libraryQuestionId;
    if (i !== exceptIdx && id && id !== "ERROR") ids.add(id);
  });
  (Array.isArray(g.testModeUsedQuestionIds) ? g.testModeUsedQuestionIds : []).forEach((id) => id && ids.add(id));
  return ids;
}

/** Venue usage predicate — matches Quizmaster advance() */
export function wasUsedAtVenue(usageHistory, venueId) {
  // Honours the host's "don't repeat questions at this venue" window (forever by default).
  return usedAtVenueRecently(usageHistory, venueId);
}

/**
 * Collect tag stats: normalized tag -> { sumRating, count, availableCount }
 */
function normalizeLibTag(t) {
  return String(t || "")
    .trim()
    .toLowerCase()
    .replace(/^#+/, "");
}

// Slide types that never pull a question from the library (matches Quizmaster.js).
const NON_PLACEHOLDER_TYPES = new Set([
  "STARTROUND", "ENDROUND", "SHOWROUNDS", "JOIN QUIZ", "INTRODUCTION", "END QUIZ",
  "BREAK TIME", "DISPLAY", "SLIDE", "DEFAULT", "DEFAULT CATEGORY",
]);

/**
 * How many #placeholder questions of each type the round starting at startRoundIdx needs filled,
 * e.g. { MULTICHOICE: 2 }. Used so the round picker only offers tags that can fill the round.
 */
export function placeholderNeedsForRound(questions, startRoundIdx) {
  const needs = {};
  if (!Array.isArray(questions)) return needs;
  for (let i = startRoundIdx + 1; i < questions.length; i++) {
    const q = questions[i] || {};
    const type = questionTypeUpper(q);
    if (type === "ENDROUND" || type === "STARTROUND" || type === "END QUIZ") break;
    const text = String(q.Q || q.question || "").trim();
    if (text.startsWith("#") && !NON_PLACEHOLDER_TYPES.has(type)) needs[type] = (needs[type] || 0) + 1;
  }
  return needs;
}

/**
 * What the round's #placeholders draw from: their genres (first hashtag) and the tags every one of
 * them requires (e.g. #Halloween), so only topics that can fill the round are offered.
 * @returns {{ genres: string[], required: string[] }} required tags are normalized
 */
export function placeholderScopeForRound(questions, startRoundIdx) {
  const genres = [];
  let required = null;
  if (!Array.isArray(questions)) return { genres, required: [] };
  for (let i = startRoundIdx + 1; i < questions.length; i++) {
    const q = questions[i] || {};
    const type = questionTypeUpper(q);
    if (type === "ENDROUND" || type === "STARTROUND" || type === "END QUIZ") break;
    const text = String(q.Q || q.question || "").trim();
    if (!text.startsWith("#") || NON_PLACEHOLDER_TYPES.has(type)) continue;
    const tags = extractHashtagsFromText(text);
    if (tags[0] && !genres.includes(tags[0])) genres.push(tags[0]);
    const rest = new Set(tags.slice(1).map(normalizeLibTag).filter(Boolean));
    required = required === null ? rest : new Set([...required].filter((t) => rest.has(t)));
  }
  return { genres, required: required ? [...required] : [] };
}

/**
 * A tag qualifies when it has minQuestionsPerTag available questions and, for every question type
 * in needByType, at least that many available questions of that type (so whatever tag the team
 * picks can fill the round's placeholders).
 * With a `scope` (the round's placeholders, from placeholderScopeForRound) only questions in those
 * genres carrying all their required tags count, and the required tags themselves aren't offered;
 * `excludeIds` are library questions already used in this game.
 * @returns {Promise<{ tag: string, avgRating: number, availableCount: number }[]>}
 */
export async function buildRankedTagOptionsForBucket({
  genreBucketKey,
  venue,
  minQuestionsPerTag = 5,
  maxOptions = 6,
  consumedTags = [],
  isTestingVenue = false,
  needByType = {},
  scope = null,
  excludeIds = null,
}) {
  const required = scope?.required || [];
  const consumedSet = new Set([...consumedTags, ...required].map((t) => normalizeLibTag(t)));
  const exclude = excludeIds instanceof Set ? excludeIds : new Set(excludeIds || []);

  const agg = new Map();
  const availByType = new Map(); // tag -> { TYPE: count }

  const ingestDoc = (id, data) => {
    const acc = String(data.accuracyFlag || "").trim();
    if (acc !== "") return;
    const rawTags = Array.isArray(data.tags) ? data.tags : [];
    const tagNorms = [...new Set(rawTags.map(normalizeLibTag).filter(Boolean))];
    if (required.some((t) => !tagNorms.includes(t))) return;
    const rating = typeof data.interestRating === "number" ? data.interestRating : 0;
    const usageHistory = data.usageHistory || [];
    const available =
      (isTestingVenue || !wasUsedAtVenue(usageHistory, venue)) && !exclude.has(id);

    const docType = String(data.questionType || "").trim().toUpperCase();
    tagNorms.forEach((tn) => {
      if (consumedSet.has(tn)) return;
      let row = agg.get(tn);
      if (!row) {
        row = { sumRating: 0, count: 0, avail: 0 };
        agg.set(tn, row);
      }
      row.sumRating += rating;
      row.count += 1;
      if (available) {
        row.avail += 1;
        const byType = availByType.get(tn) || {};
        byType[docType] = (byType[docType] || 0) + 1;
        availByType.set(tn, byType);
      }
    });
  };

  // The round's placeholder genres when known, else the bucket (every genre for "global").
  const genresToScan =
    scope?.genres?.length > 0
      ? scope.genres
      : genreBucketKey === "global"
        ? ROUND_TAG_GENRE_OPTIONS
        : [genreBucketKey];
  for (const genre of genresToScan) {
    const qy = query(
      collection(db, "QuestionLibrary"),
      where("genre", "==", genre),
      where("accuracyFlag", "==", ""),
      limit(genreBucketKey === "global" ? 800 : 2000)
    );
    try {
      const snap = await getDocs(qy);
      snap.forEach((d) => ingestDoc(d.id, d.data()));
    } catch (e) {
      console.warn(`Team choice tag scan failed for genre "${genre}":`, e);
    }
  }

  const ranked = [];
  agg.forEach((row, tagNorm) => {
    if (row.avail < minQuestionsPerTag) return;
    const byType = availByType.get(tagNorm) || {};
    if (Object.entries(needByType || {}).some(([type, need]) => (byType[type] || 0) < need)) return;
    const avg = row.count > 0 ? row.sumRating / row.count : 0;
    const displayTag = tagNorm;
    ranked.push({ tag: displayTag, avgRating: avg, availableCount: row.avail });
  });

  ranked.sort((a, b) => b.avgRating - a.avgRating || b.availableCount - a.availableCount);
  const totalQualifying = ranked.length;
  return { options: ranked.slice(0, maxOptions), totalQualifying };
}

/**
 * Preflight: each team-choice bucket needs enough qualifying tags. Rounds whose placeholders share a
 * theme tag (e.g. #Halloween) need one topic per round; open rounds need R+6 so there's a real choice.
 */
export async function runPreflightTeamTagPools(venue, questions, minQuestionsPerTag = 5) {
  const isTestingVenue = venue === "Test Venue";
  const warnings = [];
  const buckets = [];
  const counts = new Map(); // key -> { rounds, start }
  (Array.isArray(questions) ? questions : []).forEach((q, i) => {
    const { isTeamChoice, genreBucket } = parseStartRoundTeamChoice(q);
    if (!isTeamChoice) return;
    const scope = placeholderScopeForRound(questions, i);
    const key = scope.required.length === 0
      ? bucketKeyFromGenre(genreBucket)
      : "#" + [...scope.required].sort().join(" #");
    const c = counts.get(key);
    if (c) c.rounds += 1;
    else counts.set(key, { rounds: 1, start: i });
  });

  for (const key of [...counts.keys()].sort()) {
    const c = counts.get(key);
    const scope = placeholderScopeForRound(questions, c.start);
    const themed = scope.required.length > 0;
    const required = themed ? c.rounds : requiredTagPoolSizeForBucket(c.rounds);
    const { options, totalQualifying } = await buildRankedTagOptionsForBucket({
      genreBucketKey: themed ? "global" : key,
      venue,
      minQuestionsPerTag,
      maxOptions: 500,
      consumedTags: [],
      isTestingVenue,
      needByType: placeholderNeedsForRound(questions, c.start),
      scope: themed ? scope : null,
    });
    const ok = totalQualifying >= required;
    if (!ok) {
      warnings.push(
        `Team choice "${key}": need ${required} topics with enough questions, found ${totalQualifying}.`
      );
    }
    buckets.push({
      bucketKey: key,
      roundsR: c.rounds,
      requiredDistinctTags: required,
      availableDistinctTags: totalQualifying,
      ok,
      topTagsPreview: (options || []).slice(0, 6).map((o) => o.tag),
    });
  }

  return { buckets, warnings, counts: new Map([...counts].map(([k, v]) => [k, v.rounds])) };
}

export function csvHasTeamChoiceMarkers(questions) {
  if (!Array.isArray(questions)) return false;
  return questions.some((q) => parseStartRoundTeamChoice(q).isTeamChoice);
}

/** Next STARTROUND index after `afterIdx` that has #TeamChoice, or -1 */
export function findNextTeamChoiceStartIndex(questions, afterIdx) {
  if (!Array.isArray(questions)) return -1;
  for (let i = Math.max(0, afterIdx + 1); i < questions.length; i += 1) {
    if (parseStartRoundTeamChoice(questions[i]).isTeamChoice) return i;
  }
  return -1;
}

/** Default seconds to show the chosen round tag on host + all players after a pick */
export const DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC = 7;

/**
 * Earlier builds defaulted reveal pause to 3s; games/localStorage often still store 3.
 * Map that legacy value to the current default so picks get the intended ~7s without a host re-save.
 */
export function normalizeTeamRoundRevealPauseSec(raw) {
  const n =
    typeof raw === "number" && Number.isFinite(raw)
      ? raw
      : parseInt(String(raw ?? "").trim(), 10);
  if (!Number.isFinite(n) || Number.isNaN(n)) {
    return DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC;
  }
  const clamped = Math.min(30, Math.max(1, n));
  if (clamped === 3) {
    return DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC;
  }
  return clamped;
}

/** Read team round settings (host browser + synced to game.settings for players). */
export function getTeamRoundChoiceSettingsFromLocalStorage() {
  const rawReveal =
    parseInt(localStorage.getItem("opt_team_round_reveal_pause_sec") || String(DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC), 10) ||
    DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC;
  return {
    enabled: (localStorage.getItem("opt_team_round_choice") || "Off") === "On",
    behavior: localStorage.getItem("opt_team_round_behavior") || "fairShare",
    timeoutSec: Math.min(120, Math.max(5, parseInt(localStorage.getItem("opt_team_round_timeout_sec") || "15", 10) || 15)),
    revealPauseSec: normalizeTeamRoundRevealPauseSec(rawReveal),
    minQuestionsPerTag: Math.max(1, parseInt(localStorage.getItem("opt_team_min_questions_per_tag") || "5", 10) || 5),
  };
}

export function teamRoundChoiceSettingsForGameDoc() {
  const s = getTeamRoundChoiceSettingsFromLocalStorage();
  return {
    teamRoundChoiceEnabled: s.enabled ? "On" : "Off",
    teamRoundChoiceBehavior: s.behavior,
    teamRoundChoiceTimeoutSec: s.timeoutSec,
    teamRoundChoiceRevealPauseSec: s.revealPauseSec,
    teamMinQuestionsPerTag: s.minQuestionsPerTag,
  };
}

export function getTeamRoundChoiceSettingsFromGameData(gameData) {
  const st = gameData?.settings || {};
  return {
    enabled: st.teamRoundChoiceEnabled === "On" || st.teamRoundChoiceEnabled === true,
    behavior: st.teamRoundChoiceBehavior === "random" ? "random" : "fairShare",
    timeoutSec: typeof st.teamRoundChoiceTimeoutSec === "number" ? st.teamRoundChoiceTimeoutSec : 15,
    revealPauseSec: normalizeTeamRoundRevealPauseSec(
      typeof st.teamRoundChoiceRevealPauseSec === "number"
        ? st.teamRoundChoiceRevealPauseSec
        : DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC
    ),
    minQuestionsPerTag: typeof st.teamMinQuestionsPerTag === "number" ? st.teamMinQuestionsPerTag : 5,
  };
}

/**
 * After ENDROUND closes: schedule next team-choice STARTROUND (if any).
 */
export async function scheduleTeamRoundAfterEndRound({
  gameCode,
  questions,
  endRoundIdx,
  answersList,
  teamNames,
  settings,
}) {
  if (!gameCode || !settings?.enabled) return { scheduled: false };
  const nextStart = findNextTeamChoiceStartIndex(questions, endRoundIdx);
  if (nextStart < 0) return { scheduled: false };

  const roundStart = findRoundStartIndex(questions, endRoundIdx);
  const prevSnap = await getDoc(doc(db, "games", gameCode, "roundTagChoice", "session"));
  const prev = prevSnap.data() || {};
  const lastChooser = prev.lastChooserTeam || null;

  const eligible = computeEligibleTeamForEndedRound({
    answers: answersList,
    teamNames,
    roundStartIdx: roundStart,
    endRoundIdx,
    questions,
    behavior: settings.behavior === "random" ? "random" : "fairShare",
    lastChooserTeam: lastChooser,
  });

  // No real team to choose (only simulated test teams): "" means the topic is picked at random.
  const eligibleTeam = eligible.eligibleTeam || "";

  const tc = parseStartRoundTeamChoice(questions[nextStart]);
  const bucketKey = bucketKeyFromGenre(tc.genreBucket);

  await setDoc(
    doc(db, "games", gameCode, "roundTagChoice", "session"),
    {
      phase: "scheduled",
      endRoundIdx,
      startRoundIdx: nextStart,
      eligibleTeam,
      bucketKey,
      minQuestionsPerTag: settings.minQuestionsPerTag,
      timeoutSec: settings.timeoutSec,
      consumedTagsByBucket: prev.consumedTagsByBucket || {},
      chosenTagByStartRound: prev.chosenTagByStartRound || {},
      version: (prev.version || 0) + 1,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return { scheduled: true, nextStart, eligibleTeam };
}

/**
 * When the host reaches a #TeamChoice STARTROUND that was never scheduled by ENDROUND
 * (e.g. first team-choice slide appears before any ENDROUND in the CSV), pick a random
 * eligible team (same fair-share / random rules as a tie) and set phase "scheduled".
 * Also replaces a stale "scheduled" for a different slide index so out-of-order navigation still works.
 */
export async function scheduleTeamChoiceOnStartRoundLanding({
  gameCode,
  questions,
  currentIdx,
  settings,
}) {
  if (!gameCode || !settings?.enabled) return { scheduled: false };
  if (!Array.isArray(questions) || currentIdx < 0 || currentIdx >= questions.length) {
    return { scheduled: false };
  }
  const tc = parseStartRoundTeamChoice(questions[currentIdx]);
  if (!tc.isTeamChoice) return { scheduled: false };

  const ref = doc(db, "games", gameCode, "roundTagChoice", "session");
  const prevSnap = await getDoc(ref);
  const prev = prevSnap.data() || {};
  const chosenMap = prev.chosenTagByStartRound || {};

  const chosenHere = chosenMap[String(currentIdx)] ?? chosenMap[currentIdx];
  if (chosenHere != null && chosenHere !== "") {
    return { scheduled: false, reason: "alreadyChosen" };
  }

  if (
    prev.startRoundIdx === currentIdx &&
    ["scheduled", "awaitingPick", "revealPause"].includes(prev.phase)
  ) {
    return { scheduled: false, reason: "alreadyActive" };
  }

  if (
    (prev.phase === "awaitingPick" || prev.phase === "revealPause") &&
    prev.startRoundIdx != null &&
    prev.startRoundIdx !== currentIdx
  ) {
    return { scheduled: false, reason: "otherActive" };
  }

  const tSnap = await getDocs(collection(db, "games", gameCode, "teams"));
  const teamNames = [];
  tSnap.forEach((d) => teamNames.push(d.id));
  const pool = filterTeamsForRoundChooser(teamNames);

  const lastChooser = prev.lastChooserTeam || null;
  const useFairShare = settings.behavior !== "random";
  let chooserPool = [...pool];
  if (useFairShare && lastChooser && chooserPool.length > 1) {
    const without = chooserPool.filter((t) => t !== lastChooser);
    if (without.length > 0) chooserPool = without;
  }

  // No real team (only simulated test teams): "" means the topic is picked at random.
  const eligibleTeam = chooserPool[Math.floor(Math.random() * chooserPool.length)] || "";

  const bucketKey = bucketKeyFromGenre(tc.genreBucket);

  await setDoc(
    ref,
    {
      phase: "scheduled",
      endRoundIdx: prev.endRoundIdx ?? null,
      startRoundIdx: currentIdx,
      eligibleTeam,
      bucketKey,
      minQuestionsPerTag: settings.minQuestionsPerTag,
      timeoutSec: settings.timeoutSec,
      consumedTagsByBucket: prev.consumedTagsByBucket || {},
      chosenTagByStartRound: prev.chosenTagByStartRound || {},
      version: (prev.version || 0) + 1,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return { scheduled: true, startRoundIdx: currentIdx, eligibleTeam };
}

export function getExtraTeamChoiceTagForPlaceholder(sessionData, questions, targetIdx) {
  if (!sessionData?.chosenTagByStartRound) return null;
  const slide = getStartRoundSlideIndexForQuestion(questions, targetIdx);
  if (slide < 0) return null;
  return (
    sessionData.chosenTagByStartRound[String(slide)] ||
    sessionData.chosenTagByStartRound[slide] ||
    null
  );
}
